import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import {
  Partnership,
  PartnershipCreateUpdate,
  PartnershipDetail,
} from 'common/types/kraken-core/Partnership'
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query'
import {
  createPartnership,
  deletePartnership,
  getPartnership,
  searchPartnership,
  updatePartnership,
} from 'services/kraken-core/partnership/partnership.service'
import { QUERIES, useGetPartnershipParams, useSearchPartnershipParams } from './types'
import { useState } from 'react'

export const useSearchPartnership = (
  params: useSearchPartnershipParams
): UseQueryResult<PaginatedResponse<Partnership>> => {
  const queryFn = async (filters: useSearchPartnershipParams) => {
    const { response } = await searchPartnership({
      ...filters,
    })
    return response?.data || {}
  }
  const query = useQuery([QUERIES.SEARCH_PARTNERSHIP, params], () => queryFn(params), {
    keepPreviousData: true,
  })
  return query
}

export const useInfinitePartnerships = (
  params: Omit<useSearchPartnershipParams, 'page' | 'page_size'>
) => {
  const [allPartnerships, setAllPartnerships] = useState<Partnership[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadMore = async () => {
    if (isLoading || !hasMore) return

    setIsLoading(true)
    setError(null)

    try {
      const { response } = await searchPartnership({
        ...params,
        page: currentPage,
        page_size: currentPage === 1 ? 10 : 20, // Start with 10, then load 20 for subsequent pages
      })

      const newPartnerships = response?.data?.results || []
      const nextPage = response?.data?.next

      setHasMore(!!nextPage)

      if (newPartnerships.length === 0) {
        // No more data, but also no next page
        setHasMore(false)
      } else {
        setAllPartnerships(prev => [...prev, ...newPartnerships])
        setCurrentPage(prev => prev + 1)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load partnerships')
    } finally {
      setIsLoading(false)
    }
  }

  const reset = () => {
    setAllPartnerships([])
    setCurrentPage(1)
    setHasMore(true)
    setError(null)
  }

  return {
    partnerships: allPartnerships,
    loadMore,
    isLoading,
    error,
    hasMore,
    reset,
  }
}

export const useGetPartnership = (
  params: useGetPartnershipParams,
  options?: any
): UseQueryResult<PartnershipDetail> => {
  const queryFn = async ({ id }: useGetPartnershipParams) => {
    if (!id) return {}
    const { response } = await getPartnership(id)
    return response?.data || {}
  }
  const query = useQuery([QUERIES.GET, params], () => queryFn(params), {
    enabled: !!params.id,
    ...options,
  })
  return query
}

export const useUpdatePartnership = (): UseMutationResult<
  any,
  unknown,
  PartnershipCreateUpdate,
  unknown
> => {
  const queryClient = useQueryClient()
  const mutation = useMutation(
    async (partnership: PartnershipCreateUpdate) => {
      const { response } = await updatePartnership(partnership)
      return response?.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERIES.UPDATE])
      },
    }
  )
  return mutation
}

export const useCreatePartnership = (): UseMutationResult<
  any,
  unknown,
  PartnershipCreateUpdate,
  unknown
> => {
  const queryClient = useQueryClient()
  const mutation = useMutation(
    async (partnership: PartnershipCreateUpdate) => {
      const { response } = await createPartnership(partnership)
      return response?.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERIES.CREATE])
      },
    }
  )
  return mutation
}

export const useDeletePartnership = (): UseMutationResult<any, unknown, number, unknown> => {
  const queryClient = useQueryClient()
  const mutation = useMutation(
    async (id: number) => {
      const { response } = await deletePartnership(id)
      return response?.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERIES.DELETE])
      },
    }
  )
  return mutation
}
